import React, { useState, useEffect } from 'react';
import { Award, CheckCircle } from 'lucide-react';
import {
    DifficultySelector,
    HintSection,
    MarksInput,
    KnowledgeInput
} from './CreatorComponents';
import InlineMathField from '../math/InlineMathField';
import QuestionMediaSection from './QuestionMediaSection';
import {
    RESPONSE_TYPES,
    ResponseTypeSelector,
} from './ResponseBase';
import { ResponseInput } from './ResponseTypeInputs';

const SimpleQuestionForm = ({
    initialData = null,
    onSave,
    onCancel,
    bookId,
    availableNotes = [],
    availableQuestions = [],
    onQuickCreate, // For branching recursion if needed, though usually it's just one level
    compact = false,
    hideStatus = false,
    useFormTag = true
}) => {
    const [text, setText] = useState(initialData?.text || '');
    const [media, setMedia] = useState(initialData?.media || { type: 'none', images: [], video: null, embed: null });
    const [level, setLevel] = useState(initialData?.level || 1);
    const [hint, setHint] = useState(initialData?.hint || { text: '', audio: null, video: null });
    const [marks, setMarks] = useState(initialData?.marks || 1);
    const [noteIds, setNoteIds] = useState(initialData?.noteIds || []);
    const [knowledge, setKnowledge] = useState(initialData?.requiredKnowledge || { skills: [], concepts: [] });
    const [responseType, setResponseType] = useState(initialData?.responseType || RESPONSE_TYPES.MULTISELECT);
    const [responseData, setResponseData] = useState(initialData?.responseData || getDefaultResponseData(RESPONSE_TYPES.MULTISELECT));
    const [status, setStatus] = useState(initialData?.status || 'draft');
    const [error, setError] = useState(null);

    // Sync responseData if initialData changes (e.g. for editing)
    useEffect(() => {
        if (initialData) {
            setText(initialData.text || '');
            setMedia(initialData.media || { type: 'none', images: [], video: null, embed: null });
            setLevel(initialData.level || 1);
            setHint(initialData.hint || { text: '', audio: null, video: null });
            setMarks(initialData.marks || 1);
            setNoteIds(initialData.noteIds || []);
            setKnowledge(initialData.requiredKnowledge || { skills: [], concepts: [] });
            setResponseType(initialData.responseType || RESPONSE_TYPES.MULTISELECT);

            // Handle the different data structures for response data
            if (initialData.responseData) {
                setResponseData(initialData.responseData);
            } else {
                // Fallback for when data is flat on the question object
                setResponseData(buildResponseDataFromQuestion(initialData));
            }
            setStatus(initialData.status || 'draft');
        }
    }, [initialData]);

    function getDefaultResponseData(type) {
        switch (type) {
            case RESPONSE_TYPES.MULTISELECT:
                return { options: [{ id: `opt_${Date.now()}_1`, text: '' }, { id: `opt_${Date.now()}_2`, text: '' }], correctOptionIds: [] };
            case RESPONSE_TYPES.SHORT_ANSWER:
                return { correctAnswer: '', acceptableAnswers: [], caseSensitive: false };
            case RESPONSE_TYPES.PARAGRAPH:
                return { rubric: '', lineCount: 6 };
            case RESPONSE_TYPES.MATCHING:
                return { matchingPairs: [{ id: 'pair_1', prompt: '', match: '' }, { id: 'pair_2', prompt: '', match: '' }] };
            case RESPONSE_TYPES.ORDERING:
                return { orderingItems: [{ id: 'order_1', text: '' }, { id: 'order_2', text: '' }, { id: 'order_3', text: '' }] };
            case RESPONSE_TYPES.CALCULATION:
                return { solutionSteps: [{ id: 'step_1', text: '', hint: '' }], finalAnswer: '' };
            default:
                return {};
        }
    }

    function buildResponseDataFromQuestion(q) {
        const type = q.responseType || RESPONSE_TYPES.MULTISELECT;
        switch (type) {
            case RESPONSE_TYPES.MULTISELECT:
                return { options: q.options || [], correctOptionIds: q.correctOptionIds || [] };
            case RESPONSE_TYPES.SHORT_ANSWER:
                return { correctAnswer: q.correctAnswer || '', acceptableAnswers: q.acceptableAnswers || [], caseSensitive: q.caseSensitive || false };
            case RESPONSE_TYPES.PARAGRAPH:
                return { rubric: q.rubric || '', lineCount: q.lineCount || q.responseData?.lineCount || 0 };
            case RESPONSE_TYPES.MATCHING:
                return { matchingPairs: q.matchingPairs || [] };
            case RESPONSE_TYPES.ORDERING:
                return { orderingItems: q.orderingItems || [] };
            case RESPONSE_TYPES.CALCULATION:
                return { solutionSteps: q.solutionSteps || [], finalAnswer: q.finalAnswer || '' };
            default:
                return {};
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!text.trim()) {
            setError('Question text is required');
            return;
        }

        // Validation
        if (responseType === RESPONSE_TYPES.MULTISELECT) {
            const filledOptions = responseData.options?.filter(opt => opt.text.trim()) || [];
            if (filledOptions.length < 2) {
                setError('At least 2 options are required');
                return;
            }
            if (!responseData.correctOptionIds?.length) {
                setError('At least one correct answer must be selected');
                return;
            }
        } else if (responseType === RESPONSE_TYPES.SHORT_ANSWER) {
            if (!responseData.correctAnswer?.trim()) {
                setError('Correct answer is required');
                return;
            }
        }

        const questionData = {
            type: 'simple',
            responseType,
            status,
            text: text.trim(),
            media,
            level,
            marks,
            noteIds: (noteIds || []).filter(noteId => availableNotes.some(note => note.id === noteId)),
            requiredKnowledge: {
                skills: knowledge.skills.filter(s => s.trim()),
                concepts: knowledge.concepts.filter(c => c.trim())
            },
            hint: (hint.text?.trim() || hint.audio || hint.video) ? hint : null,
            autoGrade: responseType !== RESPONSE_TYPES.PARAGRAPH,
            // Pass the response data as is, the parent can merge it or flatten it
            responseData
        };

        onSave(questionData);
    };

    const FormContainer = useFormTag ? 'form' : 'div';
    const toggleNote = (id) => {
        setNoteIds((prev) => (
            prev.includes(id) ? prev.filter((noteId) => noteId !== id) : [...prev, id]
        ));
    };

    return (
        <FormContainer onSubmit={useFormTag ? handleSubmit : undefined} className={`space-y-4 ${compact ? 'p-0' : ''}`}>
            {error && (
                <div className="p-3 bg-red-900/30 border border-red-500 rounded-md text-red-200 text-sm">
                    {error}
                </div>
            )}

            <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                    Question Text
                </label>
                <InlineMathField
                    value={text}
                    onChange={setText}
                    rows={compact ? 2 : 3}
                    className="w-full bg-slate-700 text-sm border border-slate-600 rounded-md p-2 text-slate-100 focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter question text... (Ctrl+M for math)"
                />
            </div>

            <QuestionMediaSection
                value={media}
                onChange={setMedia}
                label="Question Media (Optional)"
                compact={compact}
            />

            <div className={compact ? 'grid grid-cols-1 gap-2' : ''}>
                <DifficultySelector value={level} onChange={setLevel} compact={compact} />
                <HintSection value={hint} onChange={setHint} bookId={bookId} compact={compact} />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <MarksInput value={marks} onChange={setMarks} compact={compact} />
            </div>

            <div className={`${compact ? 'p-2' : 'p-3'} bg-slate-700/30 rounded-lg border border-slate-600`}>
                <label className="block text-sm font-medium text-slate-300 mb-2">Attached Notes</label>
                {availableNotes.length === 0 ? (
                    <p className="text-xs text-slate-500">No shared notes available. Create notes in the Notes tab.</p>
                ) : (
                    <div className="space-y-1">
                        {availableNotes.map((note) => {
                            const checked = noteIds.includes(note.id);
                            return (
                                <label key={note.id} className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleNote(note.id)}
                                        className="h-4 w-4 rounded bg-slate-700 border-slate-500 text-cyan-500"
                                    />
                                    <span className="truncate">{note.title}</span>
                                    <span className="text-[10px] uppercase text-slate-500">{note.type}</span>
                                </label>
                            );
                        })}
                    </div>
                )}
            </div>

            <KnowledgeInput value={knowledge} onChange={setKnowledge} compact={compact} />

            <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Response Type</label>
                <ResponseTypeSelector
                    value={responseType}
                    onChange={(type) => {
                        setResponseType(type);
                        setResponseData(getDefaultResponseData(type));
                    }}
                    compact={compact}
                />
            </div>

            <ResponseInput
                responseType={responseType}
                responseData={responseData}
                setResponseData={setResponseData}
                availableQuestions={availableQuestions}
                onQuickCreate={onQuickCreate}
                compact={compact}
                renderBranchCreator={(onSave, onCancel) => (
                    <div className="border border-indigo-500/30 rounded-lg p-3 bg-slate-800/50">
                        <SimpleQuestionForm
                            compact
                            onSave={onSave}
                            onCancel={onCancel}
                            bookId={bookId}
                            availableNotes={availableNotes}
                            availableQuestions={availableQuestions}
                            onQuickCreate={onQuickCreate}
                            useFormTag={false}
                        />
                    </div>
                )}
            />

            {!hideStatus && (
                <div className="space-y-3 pt-2 border-t border-slate-600">
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-300">Status</span>
                        <div className="flex rounded-lg overflow-hidden border border-slate-600">
                            <button
                                type="button"
                                onClick={() => setStatus('draft')}
                                className={`px-4 py-1.5 text-sm font-medium transition ${status === 'draft'
                                    ? 'bg-amber-500 text-white'
                                    : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                                    }`}
                            >
                                Draft
                            </button>
                            <button
                                type="button"
                                onClick={() => setStatus('published')}
                                className={`px-4 py-1.5 text-sm font-medium transition ${status === 'published'
                                    ? 'bg-green-500 text-white'
                                    : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                                    }`}
                            >
                                Published
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex gap-2 pt-2 border-t border-slate-600">
                <button
                    type={useFormTag ? "submit" : "button"}
                    onClick={useFormTag ? undefined : handleSubmit}
                    className={`flex-1 font-bold py-2 px-4 rounded-md transition ${status === 'published'
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-amber-600 hover:bg-amber-700 text-white'
                        }`}
                >
                    {initialData ? 'Update Question' : `Save as ${status === 'published' ? 'Published' : 'Draft'}`}
                </button>
                {onCancel && (
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-md text-sm font-medium"
                    >
                        Cancel
                    </button>
                )}
            </div>
        </FormContainer>
    );
};

export default SimpleQuestionForm;
